import { useContext, useState } from 'react';
import type { DateValue } from '@internationalized/date';
import { Button, ButtonContext, DateRangePicker as AriaDateRangePicker, DateRangePickerStateContext, Group, I18nProvider } from 'react-aria-components';
import { CalendarDays, X } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { DateSegments, RangeCalendarPopover, boundText } from './dateParts';
import { FM } from './messages';
import { FieldFoot, FieldLabel, cx, fieldClass, labelText } from './parts';
import { parseIso, useCalendarLocale } from './locale';
import type { DateRange, DateRangePickerProps, DateRangePreset } from './types';
import './styles';

type RangeValue = { start: DateValue; end: DateValue };
type Rejected = 'range' | 'reversed' | null;

function toRange(range: DateRange): RangeValue | null {
  const start = parseIso(range.from);
  const end = parseIso(range.to);
  return start && end ? { start, end } : null;
}

/** Готовые периоды: кнопки над календарём. Нажатие подставляет период и закрывает окно. */
function Presets({ presets }: { presets: readonly DateRangePreset[] }) {
  const t = useT();
  const state = useContext(DateRangePickerStateContext);
  return (
    // Кнопки внутри окна иначе становятся «вторым триггером» поля через контекст.
    <ButtonContext.Provider value={{}}>
      <div className="fm-presets" role="group" aria-label={t(FM.rangePresets)}>
        {presets.map((preset) => (
          <Button
            key={preset.id}
            className="fm-preset"
            onPress={() => {
              const next = toRange(preset.range);
              if (!next || !state) return;
              state.setValue(next as never);
              state.close();
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </ButtonContext.Provider>
  );
}

/**
 * Период «с — по»: два набора сегментов в одном поле и один календарь с
 * выделением диапазона. Наружу — пара ISO. Период, где конец раньше начала или
 * выходит за `minValue`/`maxValue`, наружу НЕ уходит: поле показывает ошибку.
 * Пока заполнена одна граница, `onChange` не вызывается (диапазон не полон).
 */
export function DateRangePicker({
  label,
  'aria-label': ariaLabel,
  explain,
  description,
  errorMessage,
  isInvalid,
  isRequired,
  isDisabled,
  size = 'md',
  className,
  value,
  onChange,
  minValue,
  maxValue,
  isClearable = false,
  presets,
  plan,
}: DateRangePickerProps) {
  const t = useT();
  const locale = useCalendarLocale();
  const min = parseIso(minValue);
  const max = parseIso(maxValue);
  const parsed = toRange(value);

  const key = `${value.from ?? ''}|${value.to ?? ''}`;
  const [seen, setSeen] = useState(key);
  const [draft, setDraft] = useState<RangeValue | null>(parsed);
  const [rejected, setRejected] = useState<Rejected>(null);
  if (seen !== key) {
    setSeen(key);
    setDraft(parsed);
    setRejected(null);
  }

  const handle = (next: RangeValue | null) => {
    if (next) {
      if (next.end.compare(next.start) < 0) {
        setDraft(next);
        setRejected('reversed');
        return;
      }
      if ((min && next.start.compare(min) < 0) || (max && next.end.compare(max) > 0)) {
        setDraft(next);
        setRejected('range');
        return;
      }
    }
    setDraft(next);
    setRejected(null);
    onChange({ from: next ? next.start.toString() : null, to: next ? next.end.toString() : null });
  };

  let message = errorMessage;
  if (rejected === 'reversed') message = t(FM.rangeReversed);
  else if (rejected === 'range') {
    message = minValue && maxValue ? t(FM.dateOutOfRange, { min: boundText(minValue), max: boundText(maxValue) }) : minValue ? t(FM.dateNotBefore, { min: boundText(minValue) }) : t(FM.dateNotAfter, { max: boundText(maxValue!) });
  }
  const invalid = Boolean(isInvalid) || rejected !== null;
  const planDiffers = plan ? plan.from !== value.from || plan.to !== value.to : false;

  return (
    <I18nProvider locale={locale}>
      <AriaDateRangePicker
        className={fieldClass(size, className, 'ac-date', 'fm-range')}
        value={draft as never}
        onChange={handle as never}
        minValue={min ?? undefined}
        maxValue={max ?? undefined}
        isDisabled={isDisabled}
        isInvalid={invalid}
        isRequired={isRequired}
        validationBehavior="aria"
        granularity="day"
        firstDayOfWeek="mon"
        shouldForceLeadingZeros
        aria-label={label ? undefined : ariaLabel}
      >
        <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
        <Group className="ac-trigger">
          <DateSegments slot="start" />
          <span aria-hidden="true" className="fm-range-dash">
            –
          </span>
          <DateSegments slot="end" />
          {isClearable && draft ? (
            <Button
              className="fm-date-clear"
              aria-label={t(FM.dateClear)}
              slot={null}
              onPress={() => {
                setDraft(null);
                setRejected(null);
                onChange({ from: null, to: null });
              }}
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </Button>
          ) : null}
          <Button aria-label={t(FM.dateOpen)} className={cx(!(isClearable && draft) && 'fm-date-clear')}>
            <CalendarDays size={17} />
          </Button>
        </Group>
        <FieldFoot description={description} errorMessage={message} isInvalid={invalid} />
        {plan ? (
          <div className="fm-plan">
            <span>{t(FM.rangePlan, { from: boundText(plan.from), to: boundText(plan.to) })}</span>
            {planDiffers && !isDisabled ? (
              <Button slot={null} className="fm-linkbtn" onPress={() => handle(toRange(plan))}>
                {t(FM.rangeApplyPlan)}
              </Button>
            ) : null}
          </div>
        ) : null}
        <RangeCalendarPopover ariaLabel={t(FM.datePick, { field: labelText(label, ariaLabel, t(FM.dateFallback)) })} presets={presets && presets.length > 0 ? <Presets presets={presets} /> : undefined} />
      </AriaDateRangePicker>
    </I18nProvider>
  );
}
